import {
  ChangeDetectionStrategy,
  Component,
  computed,
  contentChildren,
  effect,
  inject,
  input,
  output,
  signal,
} from '@angular/core';
import { NgTemplateOutlet } from '@angular/common';
import { TranslocoDirective } from '@jsverse/transloco';
import { ButtonComponent } from '../../misc';
import { NgbPopover } from '@ng-bootstrap/ng-bootstrap';
import { FormGroup } from '@angular/forms';
import { TrackBreakpointService } from '@core-utils';
import { FilterTemplateDirective } from '../../directives';
import { TableFilterConfigInterface } from '../../types';

@Component({
  selector: 'ui-table-filters',
  imports: [TranslocoDirective, NgTemplateOutlet, ButtonComponent, NgbPopover],
  templateUrl: './table-filters.component.html',
  styleUrl: './table-filters.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  exportAs: 'tableFilters',
  providers: [TrackBreakpointService],
})
export class TableFiltersComponent {
  private trackBreakpointService = inject(TrackBreakpointService);

  form = input.required<FormGroup>();
  clearButtonText = input<string>('offers.table.clearFilters');
  filterButtonTooltip = input<string>('');
  visibleFiltersCount = input<number>(3);
  isTranslate = input<boolean>(true);

  clearFilters = output<void>();

  filterTemplates = contentChildren(FilterTemplateDirective);

  closedControls = signal<Set<string>>(new Set());
  isMobileScreen = this.trackBreakpointService.isMobileScreen;

  gridColumnsClass = computed(() => {
    const visibleCount = this.getVisibleFilters().length;
    if (visibleCount === 1) return 'grid-cols-1';
    if (visibleCount === 2) return 'grid-cols-2';
    return 'grid-cols-3';
  });

  constructor() {
    effect(() => {
      const form = this.form();
      if (form) {
        const controls = new Set<string>();
        Object.keys(form.controls).forEach((controlName) => {
          const value = form.get(controlName)?.value;
          if (this.hasControlValue(value)) {
            controls.add(controlName);
          }
        });
        this.closedControls.set(controls);
      }
    });
  }

  getFilterConfigs(): TableFilterConfigInterface[] {
    return this.filterTemplates().map((directive, index) => ({
      controlName: directive.controlName(),
      template: directive.templateRef,
      alwaysVisible: index < this.visibleFiltersCount(),
    }));
  }

  getVisibleFilters(): TableFilterConfigInterface[] {
    const configs = this.getFilterConfigs();
    return configs.filter(
      (config) =>
        config.alwaysVisible || this.closedControls().has(config.controlName),
    );
  }

  getHiddenFilters(): TableFilterConfigInterface[] {
    const configs = this.getFilterConfigs();
    return configs.filter(
      (config) =>
        !config.alwaysVisible && !this.closedControls().has(config.controlName),
    );
  }

  hasHiddenFilters(): boolean {
    return this.getHiddenFilters().length > 0;
  }

  hasValue(controlName: string): boolean {
    const value = this.form().get(controlName)?.value;
    return (
      this.closedControls().has(controlName) && this.hasControlValue(value)
    );
  }

  hasAnyValue(): boolean {
    const form = this.form();
    return Object.keys(form.controls).some((controlName) => {
      const value = form.get(controlName)?.value;
      return this.hasControlValue(value);
    });
  }

  onCloseSelect(controlName: string): void {
    const value = this.form().get(controlName)?.value;
    if (this.hasControlValue(value)) {
      const updated = new Set(this.closedControls());
      updated.add(controlName);
      this.closedControls.set(updated);
    }
  }

  onClearFilters(): void {
    this.closedControls.set(new Set());
    this.clearFilters.emit();
  }

  private hasControlValue(value: string): boolean {
    if (value === null || value === undefined || value === '') {
      return false;
    }
    if (Array.isArray(value)) {
      return value.length > 0;
    }
    return true;
  }
}
